/**
 * Shared order-status pipeline for the dine-in flow.
 *
 * The strings are persisted to `RestaurantOrder.status` as-is — the
 * live API treats `status` as free-text today (§29 will lock the
 * enum server-side).
 *
 *   Placed         → customer submitted (via QR dine-in or POS).
 *   WaiterAssigned → auto-assigned to a waiter, awaiting confirmation.
 *   WaiterConfirmed→ waiter acknowledged; ticket now visible to kitchen.
 *   Accepted       → kitchen acknowledged (KDS "Accept" button).
 *   Cooking        → kitchen started preparing.
 *   Ready          → food at the pass; waiter to pick up.
 *   Delivered      → waiter handed plates to the table (== "Served").
 *   Served         → kept for legacy back-compat. Treated identically to Delivered.
 *   Billed         → bill issued (consolidated invoice generated).
 *   Paid           → customer paid the consolidated invoice.
 *   Cancelled      → before kitchen accepts.
 *   Voided         → after kitchen accepts; needs manager auth.
 */
export const ORDER_STATUSES = [
  'Placed',
  'WaiterAssigned',
  'WaiterConfirmed',
  'Accepted',
  'Cooking',
  'Ready',
  'Delivered',
  'Served',
  'Billed',
  'Paid',
  'Cancelled',
  'Voided',
] as const;

export type RestaurantOrderStatus = (typeof ORDER_STATUSES)[number];

/** Steps the customer sees as a horizontal progress timeline. */
export const CUSTOMER_VISIBLE_STEPS: RestaurantOrderStatus[] = [
  'Placed',
  'WaiterConfirmed',
  'Cooking',
  'Ready',
  'Delivered',
];

/** Which states are "open" (still need kitchen / waiter attention). */
export const OPEN_STATES: ReadonlySet<RestaurantOrderStatus> = new Set([
  'Placed',
  'WaiterAssigned',
  'WaiterConfirmed',
  'Accepted',
  'Cooking',
  'Ready',
]);

/** Statuses where the kitchen is the actor. KDS shows only these. */
export const KITCHEN_OPEN_STATES: ReadonlySet<RestaurantOrderStatus> = new Set([
  'WaiterConfirmed',
  'Accepted',
  'Cooking',
]);

/** Statuses where the waiter is the actor. WaiterQueue shows these.
 *  `Placed` is included so a brand-new order is immediately visible for
 *  pickup/assignment (otherwise a just-placed order before auto-assign
 *  shows on NO board — admin/waiter would see nothing). */
export const WAITER_OPEN_STATES: ReadonlySet<RestaurantOrderStatus> = new Set([
  'Placed',
  'WaiterAssigned',
  'Ready',
]);

/**
 * Returns the next status when the kitchen / waiter advances the order.
 * The pipeline is linear and a missing transition returns null.
 *
 *   Placed → WaiterAssigned → WaiterConfirmed (waiter)
 *   WaiterConfirmed → Accepted → Cooking → Ready (kitchen)
 *   Ready → Delivered (waiter)
 *   Delivered → Billed → Paid (cashier)
 */
export function nextStatus(current: RestaurantOrderStatus): RestaurantOrderStatus | null {
  switch (current) {
    case 'Placed':
      return 'WaiterAssigned';
    case 'WaiterAssigned':
      return 'WaiterConfirmed';
    case 'WaiterConfirmed':
      return 'Accepted';
    case 'Accepted':
      return 'Cooking';
    case 'Cooking':
      return 'Ready';
    case 'Ready':
      return 'Delivered';
    case 'Delivered':
      return 'Billed';
    case 'Served':
      // legacy back-compat — treat as Delivered.
      return 'Billed';
    case 'Billed':
      return 'Paid';
    default:
      return null;
  }
}

/** Returns true when the order is still in the kitchen pipeline. */
export function isActiveOrder(status: string | null | undefined): boolean {
  return !!status && OPEN_STATES.has(status as RestaurantOrderStatus);
}

/** Colour token used by the panel KDS and the customer timeline. */
export function statusColor(status: RestaurantOrderStatus): {
  fg: string;
  bg: string;
  label: string;
} {
  switch (status) {
    case 'Placed':
      return { fg: '#1d4ed8', bg: '#dbeafe', label: 'Placed' };
    case 'WaiterAssigned':
      return { fg: '#7c3aed', bg: '#ede9fe', label: 'Waiter Notified' };
    case 'WaiterConfirmed':
      return { fg: '#1d4ed8', bg: '#dbeafe', label: 'Confirmed' };
    case 'Accepted':
      return { fg: '#9a3412', bg: '#ffedd5', label: 'Accepted' };
    case 'Cooking':
      return { fg: '#9a3412', bg: '#fde68a', label: 'Cooking' };
    case 'Ready':
      return { fg: '#166534', bg: '#bbf7d0', label: 'Ready' };
    case 'Delivered':
      return { fg: '#166534', bg: '#dcfce7', label: 'Delivered' };
    case 'Served':
      return { fg: '#166534', bg: '#dcfce7', label: 'Served' };
    case 'Billed':
      return { fg: '#3f3f46', bg: '#e4e4e7', label: 'Billed' };
    case 'Paid':
      return { fg: '#166534', bg: '#dcfce7', label: 'Paid' };
    case 'Cancelled':
      return { fg: '#991b1b', bg: '#fee2e2', label: 'Cancelled' };
    case 'Voided':
      return { fg: '#991b1b', bg: '#fee2e2', label: 'Voided' };
  }
}
